import { StrictMode, useEffect, useState, useRef, forwardRef } from 'react';
import { Icon } from 'components/UI/Icons';
import { Svgs } from 'environment';
import { Button } from '../../components/Button';
import { Menu } from '@headlessui/react';
import { useProjectData } from '../data/useProjectData/useProjectData';
import { ROUTE_MAP } from '../utils/routeMap';
import { ErrorModal } from '../ErrorModal';
import { EntryHistoryModal } from '../smart-components/entry-history-modal/EntryHistoryModal';
import { Entry, Variable } from '../types';
import { EntryDetailsModal } from '../smart-components/EntryDetailsModal';
import { useTracking } from 'app/tracking/TrackingProvider';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { EntryHeader } from '../component/EntryHeader';
import { ConfirmDiffModal } from '../confirm-diff-modal/ConfirmDiffModal';
import { useGetFormsQuery } from '../data/useGetFormsQuery/useGetFormsQuery';
import { useGetLatestDataEntryVersionQuery } from '../data/useGetLatestDataEntryVersionQuery';
import { useGetProjectQuery } from '../data/useGetProjectQuery';
import { useUpdateDataEntryMutation } from '../data/useUpdateDataEntryMutation';
import { createSubmitEvent, EntryForm, NextAction } from '../EntryForm';
import { SelectForm, useFormSelector } from '../smart-components/SelectForm';
import { DiffedField } from '../utils/formUtils';
import { parseBackendValues } from '../utils/parse-backend-values/parseBackendValues';
import { DeleteEntryModal } from '../smart-components/DeleteEntryModal';
import { SplitButton } from '../component/SplitButton';
import { SelectStatusButton } from '../smart-components/SelectStatusButton';
import { MoreIcon } from '@icons';
import { useEntries } from 'hooks/store/data/entries/useEntries';
import { IconButton } from '../component/IconButton';
import { IdCard } from 'lucide-react';
import { InputSkeleton } from '../component/Input';
import { CodelistSidebar } from 'features/codelist/sidebar/CodelistSidebar';
import { useIsCodelistEnabledForProject } from 'features/codelist/useIsCodeListEnabled';
import { Tooltip } from 'features/codelist/sidebar/display/Tooltip';
import { useLanguage } from 'features/hooks/useLanguage';
import { openInNewTab } from 'features/communication-bridge/openInNewTab';

export const UpdateEntryPageV1_5 = () => {
	const { track } = useTracking();

	const isCodeListActive = useIsCodelistEnabledForProject();

	const navigate = useNavigate();

	/**
	 * This is used to allow the user to leave the page without the diff modal.
	 * Used to override the behavior of usePrompt in EntryForm
	 */
	const allowLeavePageOverrideRef = useRef<boolean>(false);

	const routeParams = useParams();
	const entryId = routeParams.entryId as string;
	const projectId = routeParams.projectId as string;

	const [_, fetchEntries] = useEntries({ lazy: true, filtered: true });

	const {
		formId,
		shouldShowFormSelector: showFormSelector,
		toggleShowFormSelector,
		setSelectedFormId,
		getFormsQueryIsLoading,
		defaultFormId
	} = useFormSelector({
		projectId
	});

	const projectDataQuery = useProjectData({
		projectId,
		selectedFormId: formId
	});

	const updateDataEntryMutation = useUpdateDataEntryMutation();

	const [dataPendingSubmission, setDataPendingSubmission] = useState<DataPendingSubmission>();
	const [isFormDirty, setIsFormDirty] = useState(false);

	const dataEntryQuery = useGetLatestDataEntryVersionQuery({
		entryId,
		projectId
	});

	useEffect(() => {
		// Make sure the url matches the latest entry id so updates are most likely to work
		if (dataEntryQuery.data && dataEntryQuery.data.entry.datasetentryid !== entryId) {
			navigate(
				ROUTE_MAP.projects.byId.dataset.update.createPath({
					projectId,
					entryId: dataEntryQuery.data.entry.datasetentryid,

					// defaultFormId is needed because of a race condition where this is called without formId before the search params are updated in useFormSelector
					formId: formId || defaultFormId
				}),
				{ replace: true }
			);
		}
	}, [dataEntryQuery.data]);

	const loading =
		projectDataQuery.isLoading || dataEntryQuery.isLoading || getFormsQueryIsLoading;
	const error = projectDataQuery.error || dataEntryQuery.error;

	const [showCodeList, setShowCodeList] = useProjectSessionStorage('showCodeListModal', {
		projectId,
		initialValue: false
	});

	const { scrollToLastVisibleElement } = useLastVisibleElement({
		selector: '[data-scroll-anchor]',
		storageKey: `last-visible-element_projectId:${projectId}_entryId:${entryId}_formId:${formId}`,
		loading
	});

	useEffect(() => {
		if (!loading) {
			scrollToLastVisibleElement();
		}
	}, [loading]);

	const submitEntry = async (args: { entry: Entry; action: NextAction }) => {
		if (!projectDataQuery.data) {
			console.error(
				"onSubmit called but projectDataQuery.data doesn't exist, this is a no-op"
			);
			return;
		}

		try {
			const result = await updateDataEntryMutation.mutate({
				projectId,
				entryId,
				entry: args.entry
			});

			fetchEntries();

			allowLeavePageOverrideRef.current = true;

			if (args.action.type === 'saveAndNavigate') {
				navigate(args.action.nextNavLocation, { replace: true });
				return;
			}
			if (args.action.type === 'saveAndClose') {
				navigate(ROUTE_MAP.projects.byId.dataset.createPath({ projectId }), {
					replace: true
				});
				return;
			}

			navigate(
				ROUTE_MAP.projects.byId.dataset.update.createPath({
					projectId,
					entryId: result.updatedEntry.datasetentryid,
					formId
				}),
				{ replace: true }
			);

			await dataEntryQuery.refetch();
		} catch (_e) {
			// Error is handled in the ErrorModal
		} finally {
			track({
				eventName: 'entry_updated',
				data: {
					entryHasStatus: false,
					action: args.action
				}
			});

			setDataPendingSubmission(undefined);

			allowLeavePageOverrideRef.current = false;
		}
	};

	if (error) {
		return (
			<ErrorLoadingEntry
				isFetching={projectDataQuery.isFetching}
				refetch={() => {
					projectDataQuery.refetch();
					dataEntryQuery.refetch();
				}}
			/>
		);
	}

	return (
		<StrictMode>
			{isCodeListActive && (
				<CodelistSidebar
					entryId={entryId}
					open={showCodeList}
					onClose={() => setShowCodeList(false)}
				/>
			)}

			{/** Same height as the header */}
			<div className="h-[60px]" />

			<div className="flex flex-col mt-20">
				{!loading && (
					<SelectForm
						expanded={showFormSelector}
						onFormSelected={setSelectedFormId}
						selectedFormId={formId}
					/>
				)}
				<div
					className="flex flex-col lg:w-[756px] lg:mx-auto px-4 md:px-10"
					data-testid="update-entry-page"
				>
					<ErrorModal
						onClose={updateDataEntryMutation.reset}
						error={updateDataEntryMutation.error}
					/>

					{loading && <EntryFormSkeleton />}

					{
						// this loading seems redundant, but because of the quirk with formsQuery.isFetchedAfterMount, it caused the page both show skeleton and the data.
						// Putting this here prevents such inconsistencies
						!loading && dataEntryQuery.data && projectDataQuery.data && (
							<>
								<SubmitDiffModal
									dataPendingSubmission={dataPendingSubmission}
									initialEntry={dataEntryQuery.data.entry}
									onClose={() => setDataPendingSubmission(undefined)}
									onConfirm={submitEntry}
									onDelete={() => {
										allowLeavePageOverrideRef.current = true;

										navigate(
											ROUTE_MAP.projects.byId.dataset.createPath({
												projectId
											})
										);
									}}
									submitting={updateDataEntryMutation.loading}
									variables={projectDataQuery.data.variables}
								/>

								<EntryForm
									key={dataEntryQuery.data?.entry?.datasetentryid + formId}
									allowLeavePageOverrideRef={allowLeavePageOverrideRef}
									disabled={!dataEntryQuery.data?.entryAccess.write}
									onDirtyChange={setIsFormDirty}
									Header={
										<Header
											entryIsSubmitting={updateDataEntryMutation.loading}
											formSelectorIsShowing={showFormSelector}
											onShowFormSelectorClicked={toggleShowFormSelector}
											onEntryDeleted={() => {
												allowLeavePageOverrideRef.current = true;

												navigate(
													ROUTE_MAP.projects.byId.dataset.createPath({
														projectId
													})
												);
											}}
											onCodeListClicked={() => {
												setShowCodeList(!showCodeList);
											}}
											showCodeList={showCodeList}
											isFormDirty={isFormDirty}
										/>
									}
									projectData={projectDataQuery.data}
									initialEntry={dataEntryQuery.data.entry}
									submitting={updateDataEntryMutation.loading}
									onSubmitForConfirmation={async ({ nextAction, dirtyData }) => {
										setDataPendingSubmission({
											entry: dirtyData,
											actionType: nextAction.type
										});
									}}
									onSubmitForSubmission={async ({ nextAction, dirtyData }) => {
										await submitEntry({
											entry: dirtyData,
											action: nextAction
										});
									}}
								/>
							</>
						)
					}
				</div>
			</div>
		</StrictMode>
	);
};

const Header = ({
	entryIsSubmitting,
	onShowFormSelectorClicked,
	formSelectorIsShowing,
	onEntryDeleted,
	onCodeListClicked,
	showCodeList,
	isFormDirty
}: {
	entryIsSubmitting: boolean;
	onShowFormSelectorClicked: () => void;
	formSelectorIsShowing: boolean;
	onEntryDeleted: () => void;
	onCodeListClicked: () => void;
	showCodeList: boolean;
	isFormDirty: boolean;
}) => {
	const params = useParams();
	const projectId = params.projectId as string;
	const entryId = params.entryId as string;

	const language = useLanguage();
	const translation = DICTIONARY[language];

	const isCodeListEnabled = useIsCodelistEnabledForProject();

	const { formId } = useFormSelector({
		projectId
	});

	const getProjectQuery = useGetProjectQuery({ projectId });

	const hasCodelistReadAccess =
		getProjectQuery.data?.project.userAccess.accessCodelistRead ?? false;

	const getFormsQuery = useGetFormsQuery({
		projectId
	});

	const dataEntryQuery = useGetLatestDataEntryVersionQuery({
		entryId,
		projectId
	});

	return (
		<>
			<EntryHeader title={translation.title} breadCrumbs={[]}>
				<div className="flex gap-4 items-center">
					<Link
						to={ROUTE_MAP.projects.byId.dataset.create.createPath({
							projectId,
							formId
						})}
					>
						<Button
							variant="secondary"
							text={translation.createNewButton}
							type="button"
						/>
					</Link>

					{getFormsQuery.data && getFormsQuery.data.activeForms.length > 1 && (
						<button
							onClick={onShowFormSelectorClicked}
							className="self-end"
							type="button"
						>
							<Icon
								svg={Svgs.FileText}
								active={formSelectorIsShowing}
								propagate
								variant={v => v.button}
							/>
						</button>
					)}

					{isCodeListEnabled && (
						<Tooltip
							className="max-w-fit"
							content={
								!hasCodelistReadAccess
									? translation.codelist.disabled_description
									: translation.codelist.show
							}
							placement="bottom"
						>
							<IconButton
								type="button"
								onClick={onCodeListClicked}
								active={showCodeList}
								aria-label={translation.codelist.show}
								disabled={!hasCodelistReadAccess}
							>
								<IdCard />
							</IconButton>
						</Tooltip>
					)}

					<SelectStatusButton isFormDirty={isFormDirty} />

					<OptionsMenu
						onEntryDeleted={onEntryDeleted}
						entryId={entryId}
						projectId={projectId}
						hasDeleteAccess={!!dataEntryQuery.data?.entryAccess.delete}
						handlePrint={() =>
							openInNewTab(
								ROUTE_MAP.projects.byId.dataset.update.print.createPath({
									projectId,
									entryId,
									formId
								})
							)
						}
					/>

					{dataEntryQuery.data?.entryAccess.write ? (
						<div className="flex gap-[1px]">
							<Button
								loading={entryIsSubmitting}
								className="col-start-4 col-span-2 rounded-r-none"
								text={translation.saveButton}
								variant="primary"
								data-testid="entry-form-header_save-button"
							/>

							<SplitButton className="rounded-l-none">
								<SplitButton.Item
									title={translation.saveAndCloseButton}
									data-action="saveAndClose"
									onClick={e => {
										const target = e.target as HTMLElement;
										const form = target.closest('form');

										const submitEvent = createSubmitEvent(target);

										form?.dispatchEvent(submitEvent);
									}}
								/>
							</SplitButton>
						</div>
					) : (
						<ViewOnly />
					)}

					<Link to={ROUTE_MAP.projects.byId.dataset.createPath({ projectId })}>
						<Icon
							svg={Svgs.Close}
							variant={v => v.buttonActive}
							dataTestId="entry-form-header_close-icon"
							propagate
						/>
					</Link>
				</div>
			</EntryHeader>
		</>
	);
};

type OptionsMenuButtonProps = {
	children: React.ReactNode;
	onClick: () => void;
	ariaLabel: string;
};
export const OptionsMenuButton = forwardRef<HTMLButtonElement, OptionsMenuButtonProps>(
	({ children, onClick, ariaLabel }, ref) => {
		return (
			<button
				ref={ref}
				className="hover:text-white hover:bg-primary-500 p-4 text-start text-base"
				onClick={onClick}
				aria-label={ariaLabel}
			>
				{children}
			</button>
		);
	}
);

const ViewOnly = () => (
	<div className="border-gray-700 rounded-md border px-4 py-2 group hover:cursor-help relative">
		<p>View only</p>

		<p className="absolute right-0 z-50 w-80 top-full mt-2 px-4 py-2 bg-gray-800 text-white text-sm rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 shadow-lg">
			You can only view this entry due to read-only rights
		</p>
	</div>
);

export const OptionsMenu = ({
	entryId,
	projectId,
	hasDeleteAccess,
	handlePrint,
	onEntryDeleted
}: {
	entryId: string;
	projectId: string;
	hasDeleteAccess: boolean;
	handlePrint: () => void;
	onEntryDeleted: () => void;
}) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const { track } = useTracking();

	const [showDeleteEntryModal, setShowDeleteEntryModal] = useState(false);
	const [showEntryHistoryModal, setShowEntryHistoryModal] = useState(false);
	const [showEntryDetailsModal, setShowEntryDetailsModal] = useState(false);

	return (
		<div className="relative">
			<Menu>
				<Menu.Button
					className="hover:bg-gray-700/10 rounded-full p-2"
					aria-label={translation.moreOptions.title}
					title={translation.moreOptions.title}
				>
					<MoreIcon className="text-icon-base" />
				</Menu.Button>

				<Menu.Items className="absolute overflow-hidden top-14 right-0 w-48 bg-white rounded-lg z-50 shadow-normal flex flex-col items-stretch">
					<Menu.Item>
						<OptionsMenuButton
							ariaLabel={translation.moreOptions.printEntry}
							onClick={() => {
								handlePrint();
								track({
									eventName: 'print_entry_clicked',
									data: {
										entryType: 'entry'
									}
								});
							}}
						>
							Print entry
						</OptionsMenuButton>
					</Menu.Item>

					<Menu.Item>
						<OptionsMenuButton
							onClick={() => setShowEntryDetailsModal(true)}
							ariaLabel={translation.moreOptions.entryDetails}
						>
							{translation.moreOptions.entryDetails}
						</OptionsMenuButton>
					</Menu.Item>

					<Menu.Item>
						<OptionsMenuButton
							onClick={() => setShowEntryHistoryModal(true)}
							ariaLabel={translation.moreOptions.entryHistory}
						>
							{translation.moreOptions.entryHistory}
						</OptionsMenuButton>
					</Menu.Item>

					{hasDeleteAccess && (
						<Menu.Item>
							<OptionsMenuButton
								ariaLabel={translation.moreOptions.deleteEntry}
								onClick={() => setShowDeleteEntryModal(true)}
							>
								{translation.moreOptions.deleteEntry}
							</OptionsMenuButton>
						</Menu.Item>
					)}
				</Menu.Items>
			</Menu>

			{hasDeleteAccess && (
				<DeleteEntryModal
					title={translation.deleteEntryModal.title}
					description={translation.deleteEntryModal.description}
					entryId={entryId}
					projectId={projectId}
					show={showDeleteEntryModal}
					onClose={() => setShowDeleteEntryModal(false)}
					onEntryDeleted={onEntryDeleted}
				/>
			)}
			<EntryDetailsModal
				open={showEntryDetailsModal}
				onClose={() => setShowEntryDetailsModal(false)}
				entryId={entryId}
				projectId={projectId}
			/>

			{showEntryHistoryModal && (
				<EntryHistoryModal
					open
					onClose={() => setShowEntryHistoryModal(false)}
					entryId={entryId}
					projectId={projectId}
				/>
			)}
		</div>
	);
};

export type DataPendingSubmission = {
	entry: Entry;
	actionType: 'save' | 'saveAndClose' | 'saveAndCreateNew';
};
export const SubmitDiffModal = ({
	dataPendingSubmission,
	initialEntry,
	onClose,
	onConfirm,
	onDelete,
	submitting,
	variables
}: {
	dataPendingSubmission?: DataPendingSubmission;
	initialEntry: Entry;
	onClose: () => void;
	onConfirm: (args: { entry: Entry; action: NextAction }) => void;
	submitting?: boolean;
	variables: Variable[];
	onDelete: () => void;
}) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	if (!dataPendingSubmission) {
		return null;
	}

	const initialValues = parseBackendValues({
		variables,
		entry: initialEntry
	});
	const currentValues = {
		...initialValues,
		...parseBackendValues({
			variables,
			entry: dataPendingSubmission.entry
		}) // there is a bug here if there is a file variable they can't be parsed like this but it will work because the
	};

	const diffedFields: Record<string, DiffedField> = {};
	for (const key of Object.keys(dataPendingSubmission.entry)) {
		if (variables.find(v => v.variableName === key)?.variableType === 'file') {
			diffedFields[key] = {
				from: initialValues[key],
				to: dataPendingSubmission.entry[key]
			};
		} else {
			diffedFields[key] = {
				from: initialValues[key],
				to: currentValues[key]
			};
		}
	}

	return (
		<ConfirmDiffModal
			title={translation.confirmModal.title}
			diff={diffedFields}
			onClose={onClose}
			onConfirm={() =>
				onConfirm({
					entry: dataPendingSubmission.entry,
					action: {
						type: dataPendingSubmission.actionType
					}
				})
			}
			submitting={submitting}
			variables={variables}
			onDelete={onDelete}
		/>
	);
};

export const EntryFormSkeleton = () => {
	return (
		<div
			className="grid grid-cols-2 px-10 gap-10 relative mb-52 lg:w-[756px] lg:mx-auto"
			role="progressbar"
			data-testid="entry-form-skeleton"
		>
			<HeaderSkeleton />

			<InputSkeleton />
			<InputSkeleton />
			<InputSkeleton />
			<InputSkeleton />
			<InputSkeleton />
			<InputSkeleton />
			<InputSkeleton />
			<InputSkeleton />
			<InputSkeleton />
		</div>
	);
};

export const HeaderSkeleton = () => (
	<div className="bg-gray-100 fixed top-0 right-0 left-0 h-[60px]" />
);

export const ErrorLoadingEntry = ({
	isFetching,
	refetch,
	message = 'Error loading data, please try again. Contact support if the problem persists.'
}: {
	isFetching: boolean;
	refetch: () => void;
	message?: string;
}) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const params = useParams();
	const projectId = params.projectId as string;

	return (
		<div className="v-[100vw] h-[100vh] flex items-center justify-center flex-col">
			<Icon svg={Svgs.LedidiLogo} customSize={5} />

			<p className="text-base font-bold mt-20">{message}</p>

			<div className="flex flex-col gap-4 mt-10">
				<Button
					text={translation.errorLoadingEntry.reloadButton}
					variant="primary"
					loading={isFetching}
					onClick={refetch}
				/>

				<Link
					className="text-sm underline self-center"
					to={ROUTE_MAP.projects.byId.dataset.createPath({ projectId })}
				>
					Back to dataset
				</Link>
			</div>
		</div>
	);
};

/**
 * Options for the useLastVisibleElement hook
 */
interface UseLastVisibleElementOptions {
	/** CSS selector for the elements to observe */
	selector: string;
	/** Threshold for visibility (0-1), 1 means fully visible */
	threshold?: number;
	/** Root margin for the observer */
	rootMargin?: string;
	/** Local storage key to save the last visible element ID */
	storageKey?: string;

	loading?: boolean;
}

/**
 * Hook that tracks the last fully visible element on screen and stores it
 * for restoration after navigation
 */
export const useLastVisibleElement = ({
	selector,
	threshold = 1.0,
	rootMargin = '0px',
	storageKey = 'lastVisibleElementId',
	loading
}: UseLastVisibleElementOptions) => {
	useEffect(() => {
		if (loading) {
			return;
		}

		// Skip if not in browser environment
		if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
			return;
		}

		const elementsToObserve = document.querySelectorAll(selector);
		const currentlyVisibleElements: Set<string> = new Set();

		const observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					const id = entry.target.id;

					if (!id) {
						console.warn('Element being observed has no ID:', entry.target);
						return;
					}

					if (entry.isIntersecting) {
						currentlyVisibleElements.add(id);
					} else {
						currentlyVisibleElements.delete(id);
					}
				});

				if (currentlyVisibleElements.size > 0) {
					const allElements = Array.from(document.querySelectorAll(selector)).filter(
						el => el.id && currentlyVisibleElements.has(el.id)
					);

					if (allElements.length > 0) {
						const topmostElement = allElements[0].id;

						sessionStorage.setItem(storageKey, topmostElement);
					}
				}
			},
			{
				root: null, // viewport
				rootMargin,
				threshold
			}
		);

		// Start observing all elements
		elementsToObserve.forEach(element => {
			if (element.id) {
				observer.observe(element);
			} else {
				console.warn('Element has no ID and will not be tracked:', element);
			}
		});

		return () => {
			elementsToObserve.forEach(element => {
				observer.unobserve(element);
			});
			observer.disconnect();
		};
	}, [selector, threshold, rootMargin, storageKey, loading]);

	const scrollToLastVisibleElement = () => {
		const lastVisibleElementId = sessionStorage.getItem(storageKey);

		if (lastVisibleElementId) {
			const element = document.getElementById(lastVisibleElementId);
			if (element) {
				element.style.scrollMarginTop = '80px'; // 60px from fixed header + some extra padding
				element.scrollIntoView({ block: 'start' });
				return true;
			}
		}
		return false;
	};

	return {
		scrollToLastVisibleElement
	};
};

type UseSessionStorageOptions<T> = {
	projectId: string;
	initialValue: T;
};
const useProjectSessionStorage = <T,>(
	key: string,
	{ projectId, initialValue }: UseSessionStorageOptions<T>
) => {
	const storageKey = `${key}_projectId:${projectId}`;

	const [state, setState] = useState(() => {
		const item = sessionStorage.getItem(storageKey);
		return item ? JSON.parse(item) : initialValue;
	});

	const setValue = (value: T) => {
		sessionStorage.setItem(storageKey, JSON.stringify(value));
		setState(value);
	};

	return [state, setValue];
};

const DICTIONARY = {
	EN: {
		title: 'Update Entry',
		createNewButton: 'Create new',
		saveButton: 'Save',
		saveAndCloseButton: 'Save and close',

		errorLoadingEntry: {
			reloadButton: 'Reload'
		},

		moreOptions: {
			title: 'More options',
			printEntry: 'Print entry',
			entryDetails: 'Entry details',
			entryHistory: 'Entry history',
			deleteEntry: 'Delete entry'
		},

		deleteEntryModal: {
			title: 'Delete Entry',
			description: 'Are you sure you want to delete this entry?'
		},

		confirmModal: {
			title: 'Please review the pending changes',
			diff: {
				from: 'From',
				to: 'To'
			}
		},

		codelist: {
			show: 'Show code list',
			disabled_description: 'You do not have access to view codelist'
		}
	},
	NB: {
		title: 'Oppdater oppføring',
		createNewButton: 'Opprett ny',
		saveButton: 'Lagre',
		saveAndCloseButton: 'Lagre og lukk',

		errorLoadingEntry: {
			reloadButton: 'Last på nytt'
		},

		moreOptions: {
			title: 'Flere valg',
			printEntry: 'Skriv ut oppføring',
			entryDetails: 'Oppføringsdetaljer',
			entryHistory: 'Oppføringshistorikk',
			deleteEntry: 'Slett oppføring'
		},

		deleteEntryModal: {
			title: 'Slett oppføring',
			description: 'Er du sikker på at du vil slette denne oppføringen?'
		},

		confirmModal: {
			title: 'Bekreft endringene',
			diff: {
				from: 'Fra',
				to: 'Til'
			}
		},

		codelist: {
			show: 'Vis kodeliste',
			disabled_description: 'Du har ikke tilgang til å se kodelisten'
		}
	}
} as const;
